import React, { useState } from 'react';
import { Grid, GridColumn, GridDataStateChangeEvent } from '@progress/kendo-react-grid';
import { Input } from '@progress/kendo-react-inputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import './NotificationsGrid.css';
import { Notification, NotificationGroupStatus, getNotificationGroupStatus } from '../../data/notificationData';
import { useToast } from '../../contexts/ToastContext';

interface NotificationsGridProps {
  data: {
    data: Notification[];
    total: number;
  };
  // Left out for view-only access, which hides the add button.
  onAddNew?: () => void;
  onDataStateChange: (event: GridDataStateChangeEvent) => void;
}

// Custom text filter cell defined outside to prevent recreation on each render
const TextFilterCell = (props: any) => {
  const [value, setValue] = React.useState(props.value || '');

  React.useEffect(() => {
    setValue(props.value || '');
  }, [props.value]);

  const handleChange = (e: any) => {
    const newValue = e.value;
    setValue(newValue);
    
    if (props.onChange) {
      props.onChange({
        value: newValue,
        operator: newValue ? 'contains' : '',
        syntheticEvent: e.syntheticEvent,
      });
    }
  };

  return (
    <div className="k-filtercell">
      <Input
        value={value}
        onChange={handleChange}
      />
    </div>
  );
};

const NotificationsGrid: React.FC<NotificationsGridProps> = ({ data, onAddNew, onDataStateChange }) => {
  const [groupStatusDialogVisible, setGroupStatusDialogVisible] = useState(false);
  const [groupStatuses, setGroupStatuses] = useState<NotificationGroupStatus[]>([]);
  const [loadingGroupStatus, setLoadingGroupStatus] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState<number | null>(null);
  const { showToast } = useToast();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleViewGroupStatus = async (notificationId: number) => {
    setSelectedNotificationId(notificationId);
    setLoadingGroupStatus(true);
    setGroupStatusDialogVisible(true);
    
    try {
      const statuses = await getNotificationGroupStatus(notificationId);
      setGroupStatuses(statuses);
    } catch (error) {
      console.error('Error fetching group status:', error);
      showToast('Failed to load group status', 'error');
      setGroupStatusDialogVisible(false);
    } finally {
      setLoadingGroupStatus(false);
    }
  };

  const IsReadCell = (props: any) => {
    const isRead = props.dataItem.isRead;
    const isGroupNotification = props.dataItem.recipientGroupId !== null && props.dataItem.recipientGroupId !== undefined;
    
    if (isGroupNotification) {
      return (
        <td style={{ textAlign: 'center' }}>
          <span 
            onClick={() => handleViewGroupStatus(props.dataItem.notificationId)}
            style={{
              backgroundColor: '#0078d4',
              color: 'white',
              padding: '0.25rem 0.5rem',
              borderRadius: '0.25rem',
              fontSize: '9pt',
              fontWeight: '600',
              cursor: 'pointer',
              userSelect: 'none'
            }}
          >
            View Status
          </span>
        </td>
      );
    }
    
    return (
      <td style={{ textAlign: 'center' }}>
        {isRead ? (
          <span style={{
            backgroundColor: '#28a745',
            color: 'white',
            padding: '0.25rem 0.5rem',
            borderRadius: '0.25rem',
            fontSize: '9pt',
            fontWeight: '600'
          }}>
            Read
          </span>
        ) : (
          <span style={{
            backgroundColor: '#ffc107',
            color: '#000',
            padding: '0.25rem 0.5rem',
            borderRadius: '0.25rem',
            fontSize: '9pt',
            fontWeight: '600'
          }}>
            Unread
          </span>
        )}
      </td>
    );
  };

  const DateCell = (props: any) => {
    return <td>{formatDate(props.dataItem.createdDate)}</td>;
  };

  const RecipientCell = (props: any) => {
    const recipient = props.dataItem.recipientEmployee;
    return <td>{recipient ? recipient.employeeName : 'Group Notification'}</td>;
  };

  const CreatedByCell = (props: any) => {
    const creator = props.dataItem.employeeCreatedby;
    return <td>{creator ? creator.employeeName : '-'}</td>;
  };

  return (
    <div className="notifications-grid-wrapper">
      {onAddNew && (
      <div style={{ marginBottom: '1rem' }}>
        <button
          className="form__edit__button accept"
          onClick={onAddNew}
          style={{ padding: '0.75rem 1.5rem' }}
        >
          Add New Notification
        </button>
      </div>
      )}
      <Grid
        data={data}
        pageable={{
          buttonCount: 5,
          info: true,
          type: 'numeric',
          pageSizes: [10, 25, 50, 100],
          previousNext: true,
        }}
        sortable={true}
        filterable={true}
        onDataStateChange={onDataStateChange}
        resizable={true}
        reorderable={true}
        style={{ height: 'calc(100vh - 300px)' }}
      >
        <GridColumn field="recipientName" title="Recipient" width="180px" filterCell={TextFilterCell} />
        <GridColumn field="message" title="Message" filterCell={TextFilterCell} />
        <GridColumn field="isRead" title="Status" width="120px" cell={IsReadCell} filterable={false} />
        <GridColumn field="createdDate" title="Created Date" width="140px" cell={DateCell} filterable={false} />
        <GridColumn field="createdByName" title="Created By" width="180px" filterable={false} />
      </Grid>

      {groupStatusDialogVisible && (
        <Dialog
          title="Group Notification Status"
          onClose={() => setGroupStatusDialogVisible(false)}
          width={700}
          height={500}
        >
          {loadingGroupStatus ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
              <p>Loading group status...</p>
            </div>
          ) : (
            <div style={{ padding: '1rem' }}>
              <p style={{ marginBottom: '1rem', color: '#605e5c', fontSize: '14px' }}>
                Below is the read status for each member of the group:
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f3f2f1', borderBottom: '2px solid #edebe9' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Employee Name</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Position</th>
                    <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: '600' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {groupStatuses.map((status, index) => (
                    <tr key={status.notificationGroupStatusId} style={{ borderBottom: '1px solid #edebe9' }}>
                      <td style={{ padding: '0.75rem' }}>{status.employee.employeeName}</td>
                      <td style={{ padding: '0.75rem', color: '#605e5c' }}>{status.employee.position || '-'}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        {status.isRead ? (
                          <span style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '9pt',
                            fontWeight: '600'
                          }}>
                            Read
                          </span>
                        ) : (
                          <span style={{
                            backgroundColor: '#ffc107',
                            color: '#000',
                            padding: '0.25rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '9pt',
                            fontWeight: '600'
                          }}>
                            Unread
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {groupStatuses.length === 0 && !loadingGroupStatus && (
                <p style={{ textAlign: 'center', padding: '2rem', color: '#605e5c' }}>
                  No group members found.
                </p>
              )}
            </div>
          )}
          <DialogActionsBar>
            <Button onClick={() => setGroupStatusDialogVisible(false)}>Close</Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  );
};

export default NotificationsGrid;
